import type {
  EventDateType,
  EventDate,
  EventDescription,
  EventDateChildren,
} from '~/composables/types/event'

const getDateType = (date: EventDate): EventDateType => {
  return date?.dateType || 'PUBLIC'
}

const getChildren = (date: EventDate): EventDateChildren[] => {
  return date?.children?.data || date?.children || []
}

const isMultiDate = (date: EventDate): boolean =>
  Boolean(date?.children?.length || date?.children?.data?.length)

const getIsPrivate = (date: EventDate, strict = false) => {
  if (strict) {
    return getDateType(date) === 'PRIVATE'
  }

  return getDateType(date) !== 'PUBLIC'
}

const getIsPublic = (date: EventDate, strict = false) => {
  if (strict) {
    return getDateType(date) === 'PUBLIC'
  }
  return getDateType(date) !== 'PRIVATE'
}

const getDateIsOpen = (date: EventDate) => {
  return date.status === 'OPEN'
}

const getDateIsSoldOut = (date: EventDate) => {
  return date.status === 'SOLD_OUT'
}

const getAvailableTicketsQuantity = (date: EventDate) => {
  return date?.availableTicketsQuantity || 0
}

const getPrivateMinTickets = (date?: EventDate) => {
  return date?.privateMinTickets || 1
}

const getPrivateMaxTickets = (date: EventDate) => {
  return date?.privateMaxTickets || 1
}

const getPrice = (date: EventDate) => {
  return date?.product?.price || date?.product?.data?.price || null
}

const getPriceAmount = (date?: EventDate): number => {
  return (
    (date?.product?.price?.amount as number) ||
    (date?.product?.data?.price?.amount as number) ||
    0
  )
}

const getMinimumPrivatePriceAmount = (date?: EventDate): number =>
  getPrivateMinTickets(date) * getPriceAmount(date)

const getStartTime = (date: EventDate) => {
  return date?.start || ''
}

const getEndTime = (date: EventDate) => {
  return date?.end || ''
}

const getSoldOutDatesList = (dates: EventDate[]) => {
  return toValue(dates).filter((date: EventDate) =>
    eventDateGetters.getDateIsSoldOut(date)
  )
}

const getNonSoldOutDatesList = (dates: EventDate[]) => {
  return toValue(dates).filter(
    (date: EventDate) => !eventDateGetters.getDateIsSoldOut(date)
  )
}
const getFormattedEventDatePrice = (
  event: EventDescription,
  selectedTime: EventDate | null = null
) => {
  if (selectedTime !== null) {
    return eventGetters.getPrice(selectedTime?.product)
  }

  const defaultPrice = eventGetters.getDefaultFormattedPrice(event)

  if (defaultPrice) {
    return defaultPrice
  }

  return eventGetters.getDefaultFormattedPrice(event)
}

const getOriginalPrice = (selectedTime?: EventDate) => {
  const product = selectedTime?.product
  if (!product) return undefined

  return product.original_price
}

const getOriginalPriceAmount = (selectedTime?: EventDate) => {
  return getOriginalPrice(selectedTime)?.amount
}

const getRawEventDatePrice = (
  event: EventDescription,
  selectedTime: EventDate | null = null
) => {
  if (selectedTime !== null) {
    return eventGetters.getPriceAmount(selectedTime?.product)
  }

  const defaultPrice = eventGetters.getDefaultPriceAmount(event)

  if (defaultPrice) {
    return defaultPrice
  }

  return eventGetters.getDefaultPriceAmount(event)
}

export function areAllDatesSoldOut(dates: EventDate[]) {
  return getNonSoldOutDatesList(toValue(dates)).length === 0
}

export function areAnyDatesAvailable(dates: EventDate[]) {
  return toValue(dates).length > 0
}

export const eventDateGetters = {
  getIsPublic,
  getDateType,
  getIsPrivate,
  getDateIsOpen,
  getStartTime,
  getEndTime,
  getAvailableTicketsQuantity,
  getPrivateMinTickets,
  getPrivateMaxTickets,
  getPrice,
  getPriceAmount,
  getMinimumPrivatePriceAmount,
  getDateIsSoldOut,
  getSoldOutDatesList,
  getNonSoldOutDatesList,
  getFormattedEventDatePrice,
  getRawEventDatePrice,
  areAllDatesSoldOut,
  areAnyDatesAvailable,
  getChildren,
  isMultiDate,
  getOriginalPrice,
  getOriginalPriceAmount,
}
